<script setup lang="ts">
import SimpleSpinner from "@/components/layouts/SimpleSpinner.vue";
import { FILE_TYPE_TEXTS, MAX_FILE_SIZE_TEXTS } from "@/consts";
import { FileSizes, type FileAcceptTypes } from "@/enums";
import { ref, toRefs } from "vue";

interface Props {
  icon: string;
  uploading?: boolean;
  error?: boolean | null;
  disabled?: boolean;
  accept: FileAcceptTypes;
  maxSize?: FileSizes;
  showTitle?: boolean;
  triggerClasses?: string;
}

const props = withDefaults(defineProps<Props>(), {
  error: false,
  disabled: false,
  maxSize: FileSizes.Max15MB,
  showTitle: true,
  triggerClasses: "h-140 min-h-140",
});

const emit = defineEmits<{
  click: [];
  dropped: [FileList];
}>();

const { maxSize } = toRefs(props);
const isDragging = ref(false);

const onDragOver = (event: Event) => {
  event.preventDefault();
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event: DragEvent) => {
  isDragging.value = false;

  if (!event || !event.dataTransfer) {
    return;
  }

  emit("dropped", event.dataTransfer.files);
};
</script>

<template>
  <div class="flex flex-col gap-2 w-full">
    <div v-if="showTitle" class="h100 text-neutral1000">
      {{ $t("global.from_your_computer") }}
    </div>
    <button
      type="button"
      class="rounded-lg border border-dashed border-neutral500 flex flex-col items-center justify-center b300 gap-1 w-full transition focus-visible-strong"
      :class="{
        'bg-darkOcean100 border-darkOcean600 border-2 text-darkOcean600':
          isDragging,
        'bg-neutral100 text-neutral600': !isDragging,
        'outline outline-2 outline-red400': error,
        'hover:enabled:outline-neutral500': !error,
        [triggerClasses]: true,
      }"
      :disabled="disabled"
      @dragover.prevent="onDragOver"
      @dragleave="onDragLeave"
      @drop.prevent="onDrop"
      @click="$emit('click')"
    >
      <SimpleSpinner v-if="uploading" />
      <i
        v-if="!uploading"
        class="text-2xl"
        :class="{ [icon]: true, 'text-neutral400': disabled }"
      ></i>
      <div
        v-if="!uploading && !isDragging"
        :class="{ 'text-neutral400': disabled }"
      >
        <div v-html="$t('global.click_here_or_drop')"></div>
        <div v-if="accept" class="b100 text-neutral600">
          {{ $t("global.file_type", { type: FILE_TYPE_TEXTS[accept] }) }}
        </div>
        <div v-if="maxSize" class="b100 text-neutral600">
          {{
            $t("global.file_size", {
              size: MAX_FILE_SIZE_TEXTS[maxSize || FileSizes.Max15MB],
            })
          }}
        </div>
      </div>
      <div v-if="isDragging">{{ $t("global.drop_here") }}</div>
    </button>
  </div>
</template>
